import { Box, Button, Typography, alpha, useTheme } from "@mui/material";
import React, { useState } from "react";
import { UpsellServiceTableV2 } from "./UpsellTable/UpsellServiceTableV2";
import { UpsellServiceCards } from "./UpsellTable/UpsellServiceCards";
import { Close, TrendingUp, TipsAndUpdates } from "@mui/icons-material";
import { RightPanel } from "../../../components/RightPanel";
import { ViewToggle } from "../../../components/ViewToggle";

interface IUpsellLeadsRightPanelProps {
  open: boolean;
  onClose: () => void;
  selectedVenueCategory: any;
  setSelectedVenueCategory: (venueCategory: any) => void;
  selectedBookingPeriod?: number;
}

export const UpsellLeadsRightPanel: React.FC<IUpsellLeadsRightPanelProps> = ({
  open,
  onClose,
  selectedVenueCategory,
  selectedBookingPeriod,
}) => {
  const theme = useTheme();
  const [isCardView, setIsCardView] = useState(false);

  // Determine if it's a service or venue based on available properties
  const isService = !!selectedVenueCategory?.service_category_uuid;
  const isVenue = !!selectedVenueCategory?.venue_uuid;
  const isFood = !!selectedVenueCategory?.food_item_uuid;
  const referenceType = isService ? "SERVICE" : isVenue ? "VENUE" : "FOOD_ITEM";

  // Get reference UUID from upselling_services array or upselling_venue object
  const getReferenceUuid = () => {
    const firstLead = selectedVenueCategory?.leads?.[0];

    if (!firstLead) return "";

    if (isService) {
      // Get first service item UUID from upselling_services array
      const upsellingServices = firstLead.upselling_services;
      if (Array.isArray(upsellingServices) && upsellingServices.length > 0) {
        return upsellingServices[0]?.service_item_uuid || "";
      }
      return "";
    } else if (isVenue) {
      // Get venue UUID from upselling_venue object
      return firstLead.upselling_venue?.venue_uuid || "";
    } else if (isFood) {
      // Get venue UUID from upselling_venue object
      return firstLead.upselling_food_item?.food_item_uuid || "";
    }
  };

  const referenceUuid = getReferenceUuid();

  const handleToggle = (isGridView: boolean) => {
    setIsCardView(isGridView);
  };

  return (
    <RightPanel
      open={open}
      width="90%"
      onClose={onClose}
      heading={`Leads for ${
        selectedVenueCategory?.venue_name ||
        selectedVenueCategory?.service_category_name ||
        selectedVenueCategory?.item_name
      }`}
      actionButtons={() => {
        return (
          <Button variant="contained" onClick={onClose}>
            <Close sx={{ fontSize: 18, mr: 0.5 }} />
            Close
          </Button>
        );
      }}
    >
      {/* Top Bar with Info and Toggle */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
          backgroundColor: alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TipsAndUpdates
            sx={{ fontSize: 20, color: theme.palette.primary.dark }}
          />
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            Following are leads for upsell opportunities. Click on the{"   "}
            <TrendingUp
              sx={{
                fontSize: 14,
                verticalAlign: "middle",
                color: theme.palette.primary.main,
              }}
            />
            {"    "}
            icon to create an upsell lead.
          </Typography>
        </Box>
        <ViewToggle
          isGridView={isCardView}
          onToggle={handleToggle}
          size="small"
        />
      </Box>

      {/* Toggle between Card and Table views */}
      {isCardView ? (
        <UpsellServiceCards
          leadsData={selectedVenueCategory.leads}
          referenceType={referenceType}
          referenceUuid={referenceUuid}
          selectedBookingPeriod={selectedBookingPeriod}
        />
      ) : (
        <UpsellServiceTableV2
          leadsData={selectedVenueCategory.leads}
          referenceType={referenceType}
          referenceUuid={referenceUuid}
          selectedBookingPeriod={selectedBookingPeriod}
        />
      )}
    </RightPanel>
  );
};
